import {$createCodeNode} from '@lexical/code-core';
import {$createTextNode, $getRoot, $getSelection, $isRangeSelection} from 'lexical';

const JSON_SPACES = 4;
const LANGUAGE = 'json';

const countLeftBrackets = (str?: string) => str?.match(/{|\[/g)?.length ?? 0;
const countRightBrackets = (str?: string) => str?.match(/}|]/g)?.length ?? 0;

const countBrackets = (text: string, startCount: number = 0) =>
  Math.max(startCount + countLeftBrackets(text) - countRightBrackets(text), 0);

const isEndOfBlock = (str?: string) => !!str?.match(/^\s*}|]+\s*,*\s*$/g);

const formatTextToJson = (value?: string | null) => {
  if (!value) {
    return '';
  }

  try {
    return JSON.stringify(JSON.parse(value), null, JSON_SPACES);
  } catch (e) {}

  return value;
};

const getTextBeforeSelection = () => {
  const selection = $getSelection();

  if (!$isRangeSelection(selection)) {
    return '';
  }

  const anchor = selection.anchor;
  const targetNode = anchor.getNode();

  const textNodes = [
    ...targetNode.getPreviousSiblings().map((node) => node.getTextContent()),
    targetNode.getTextContent(),
  ];

  return textNodes.join('');
};

const formatPastedText = (value?: string | null) => {
  if (!value) {
    return '';
  }

  const prevText = getTextBeforeSelection();
  let bracketsCount = countBrackets(prevText);
  const jsonText = formatTextToJson(value);

  const lines = jsonText.split('\n').map((line, index) => {
    let nextLine = line.trim();

    if (index === 0) {
      bracketsCount = countBrackets(line, bracketsCount);

      return nextLine;
    }

    if (isEndOfBlock(line)) {
      bracketsCount = countBrackets(line, bracketsCount);
      nextLine = nextLine.padStart(nextLine.length + bracketsCount * JSON_SPACES);
    } else {
      nextLine = nextLine.padStart(nextLine.length + bracketsCount * JSON_SPACES);
      bracketsCount = countBrackets(line, bracketsCount);
    }

    return nextLine;
  });

  return lines.join('\n');
};

const getNewLineText = () => {
  const prevText = getTextBeforeSelection();

  if (prevText.trim().endsWith('{') || prevText.trim().endsWith('[')) {
    return ''.padStart(JSON_SPACES);
  }

  return '';
};

const $serializeEditorToText = (): string => $getRoot().getTextContent();

const $createJsonNode = (value?: string | null, isPasted?: boolean) => {
  const codeNode = $createCodeNode(LANGUAGE);

  const formatter = isPasted ? formatPastedText : formatTextToJson;

  return codeNode.append($createTextNode(formatter(value)));
};

const $buildEditorFromText = (value?: string | null) => {
  $getRoot().clear().append($createJsonNode(value));
};

export {$serializeEditorToText, $buildEditorFromText, $createJsonNode, getNewLineText};
